import { createMemo, type Accessor } from 'solid-js';
import type {
  GpuSnapshot,
  LlamacppSnapshot,
  LoadedModelView,
  OllamaSnapshot,
  TpsPoint,
} from '../state/metrics';
import { selectedServerKey } from '../state/selection';
import { HudPanel } from '../ui/hud-panel';
import { activeServerOf } from './llamacpp-card';
import { GpuCluster } from './gpu-cluster';
import { heroView, ModelHero, vramUnattributable } from './model-hero';

/**
 * The dashboard's answer to "what is this machine doing right now": the serving
 * model on the left, the GPU it runs on to the right, in a single HUD panel.
 * Layout truth: docs/design/2026-08-02-redesign-e-stark-controls.html (.cockpit).
 */
export function Cockpit(props: {
  gpu: Accessor<GpuSnapshot | null>;
  ollama: Accessor<OllamaSnapshot | null>;
  llamacpp: Accessor<LlamacppSnapshot | null>;
  tps: Accessor<TpsPoint[]>;
  tpsByServer: Accessor<Record<string, TpsPoint[]>>;
}) {
  const devices = () => props.gpu()?.devices ?? [];
  const loaded = (): LoadedModelView[] => props.ollama()?.loaded ?? [];
  // Follows the selected server, same as the hero above it: the GPU cluster must
  // describe the process whose numbers the hero is describing, not always the
  // first/largest one, or the two halves of the cockpit could point at different
  // servers. Computed once (not inline in `serverPid`/`attributeLlamacpp`
  // separately) since both now read it — `heroView` isn't free, and the two
  // reads must agree on the same value within a render.
  const hero = createMemo(() =>
    heroView(props.llamacpp(), activeServerOf(props.llamacpp()?.servers, selectedServerKey())),
  );
  const serverPid = () => hero().vramPid;
  // Shares the exact rule the hero's own VRAM row uses (`vramUnattributable`), so
  // the GPU cluster never sums a name-matched process onto a server whose PID the
  // agent could not resolve — see `model-hero.tsx` for the full reasoning.
  const attributeLlamacpp = () => !vramUnattributable(hero());

  return (
    <HudPanel class="min-[880px]:col-span-2">
      <div class="grid gap-6 min-[880px]:grid-cols-[1.5fr_1fr] min-[880px]:items-center">
        <ModelHero data={props.llamacpp} tps={props.tps} tpsByServer={props.tpsByServer} gpu={props.gpu} />
        <GpuCluster
          devices={devices}
          loaded={loaded}
          serverPid={serverPid}
          attributeLlamacpp={attributeLlamacpp}
          hasFrame={() => props.gpu() != null}
        />
      </div>
    </HudPanel>
  );
}
